/**
 * Pure find-in-page helpers for in-lesson search (docs/135-IN-LESSON-SEARCH.md v0) — a
 * browser-style "find in this lesson" over the rendered reading WebView, distinct from
 * `search.ts`'s catalog/pack/lesson **title** search. Kept as its own file (rather than
 * folded into `search.ts`) because it operates over arbitrary lesson body text, but
 * shares the exact same case/accent-fold semantics via {@link foldMatchText} — `search.ts`'s
 * `normalizeQuery` itself builds on this fold, so list search and in-lesson find always
 * agree on what "matches."
 *
 * The reader WebView can't `import` this module (it's plain injected JS with no bundler)
 * — `src/ui/reader/findEngineScript.ts` inlines an identical copy of `foldMatchText`'s
 * algorithm; that file's own test asserts the two agree on a battery of accent/case cases,
 * so the duplication can't silently drift.
 */

/** Lowercase + Unicode NFD accent-fold (no trim — see `search.ts`'s `normalizeQuery`). */
export function foldMatchText(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/** A single match's `[start, end)` offsets within the original (unfolded) text. */
export interface MatchRange {
  start: number;
  end: number;
}

/**
 * Every non-overlapping match of `query` within `text`, fold-compared (case/accent
 * insensitive, mirroring `foldMatchText`). An empty/whitespace-only query returns no
 * matches — unlike `packMatchesQuery`'s "empty matches everything" convention, an
 * in-page find bar with an empty box should highlight nothing. Matches don't overlap:
 * after a hit, the scan resumes right after it (so `"aa"` in `"aaaa"` yields 2 hits at
 * `[0,2)`/`[2,4)`, not 3).
 *
 * Assumes the fold doesn't change the string's length, which holds for the composed-form
 * Latin diacritics this app's authored content actually uses (an accented letter's NFD
 * decomposition — base + one combining mark — collapses back to exactly one character
 * once the combining mark is stripped), so a folded-text match index applies directly to
 * the original string. An already-decomposed or otherwise unusual Unicode input could in
 * principle misalign by a character or two; an accepted v0 limitation the injected
 * WebView engine shares by construction (same algorithm).
 */
export function findMatchRanges(text: string, query: string): MatchRange[] {
  const q = foldMatchText(query.trim());
  if (!q) return [];
  const t = foldMatchText(text);
  const ranges: MatchRange[] = [];
  let from = 0;
  while (from <= t.length - q.length) {
    const idx = t.indexOf(q, from);
    if (idx === -1) break;
    ranges.push({ start: idx, end: idx + q.length });
    from = idx + q.length;
  }
  return ranges;
}
